import { defineChatActions } from './actionsContext'
import { checkAuth, globalUnreadMessages, shouldSuppressAutoReadReceipt } from './runtime'
import { initChatMessageSounds } from '@/modules/chat-matrix-fast/utils/chatMessageSound'
import { waitForMatrixChatStoreReset } from '@/modules/chat-matrix-fast/services/matrixChatCleanup'
import { clearLocallyReadTopicRooms } from '@/modules/chat-matrix-fast/utils/topics'
import { isChatBeingViewed, resolveViewedMessageRoomId } from '@/modules/chat-matrix-fast/utils/chatSurface'
import { rememberForgottenRoom } from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import {
  ensureMatrixSyncAlive,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import {
  subscribeMatrixChatUpdates,
  subscribeMatrixIncomingMessages,
  subscribeMatrixMessageRedactions,
  subscribeMatrixPeerDeleteRequests,
} from '@/modules/chat-matrix-fast/services/matrixEventSubscriptions'
import { clearMatrixSyncAuthRecoveryCircuit } from '@/modules/chat-matrix-fast/services/matrixAuthRecovery'
import {
  isMatrixClientActive,
  resetMatrixClient,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { ensureMatrixSession } from '@/modules/chat-matrix-fast/matrixSession'
import {
  getCachedMatrixLastSeenAt,
  getCachedMatrixPresence,
  resetMatrixPresenceState,
  subscribeMatrixPresence,
  trackMatrixPresence,
} from '@/modules/chat-matrix-fast/services/matrixPresenceService'
import {
  chatMatchesPeerMxid,
  collectDialogPeerMxids,
  resolveDialogPeerMxid,
} from '@/modules/chat-matrix-fast/utils/dialogPresence'

import { applyMessengerBootChatListTab } from '@/modules/chat-matrix-fast/utils/chatListPersistence'
import {
  notifyViewSyncActiveChatOnline,
  syncViewWithCore,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import {
  clearAllRoomMessages,
  clearMessages,
  dropRoomMessages,
} from '@/modules/chat-matrix-fast/stores/core/messageRoomCache'
import {
  clearAllMatrixSubscriptions,
  clearMatrixSubscription,
  setMatrixSubscription,
} from '@/modules/chat-matrix-fast/stores/matrixSubscriptions'
import { matrixSyncDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

let matrixChatInitializationPromise: Promise<boolean> | null = null
let matrixChatInitGeneration = 0
let matrixVisibilityHandler: (() => void) | null = null
let matrixOnlineHandler: (() => void) | null = null

/** Схлопываем burst membership/room → один silent fetchChats. */
let silentFetchChatsInFlight: Promise<void> | null = null
let silentFetchChatsQueued = false

function scheduleSilentFetchChats(store: {
  fetchChats: (types?: string, options?: { silent?: boolean }) => Promise<void>
}): void {
  if (silentFetchChatsInFlight) {
    silentFetchChatsQueued = true
    return
  }

  matrixSyncDebugLog('subscribeMatrixChatUpdates → silent fetchChats')
  silentFetchChatsInFlight = store.fetchChats(undefined, { silent: true })
    .catch(() => undefined)
    .finally(() => {
      silentFetchChatsInFlight = null
      if (silentFetchChatsQueued) {
        silentFetchChatsQueued = false
        scheduleSilentFetchChats(store)
      }
    })
}

export const initActions = defineChatActions({
  async initializeOnce(): Promise<boolean> {
    await waitForMatrixChatStoreReset()
    if (!checkAuth()) {
      return false
    }

    if (this.isInitialized) {
      return !this.needsMatrixAuth
    }

    if (matrixChatInitializationPromise) {
      matrixSyncDebugLog('initializeOnce → join in-flight init')
      return matrixChatInitializationPromise
    }

    const generation = matrixChatInitGeneration

    // Не чистим promise при успехе — иначе App.vue + page onMounted
    // успевают запустить второй runInitializeOnce и stopClient (canceled sync).
    const initPromise: Promise<boolean> = this.runInitializeOnce(generation)
      .finally(() => {
        if (!this.isInitialized) {
          matrixChatInitializationPromise = null
        }
      })

    matrixChatInitializationPromise = initPromise

    return initPromise
  },

  async runInitializeOnce(generation = matrixChatInitGeneration): Promise<boolean> {
    this.isInitializing = true
    this.matrixAuthError = null
    matrixSyncDebugLog('runInitializeOnce → start', { generation })

    try {
      const session = await ensureMatrixSession()
      if (!session) {
        this.needsMatrixAuth = true
        return false
      }

      if (generation !== matrixChatInitGeneration) {
        matrixSyncDebugLog('runInitializeOnce → aborted (stale generation)', { generation })
        return false
      }

      this.needsMatrixAuth = false

      // Глобальный sync (App) и /messenger могут пересечься — не убиваем живой client.
      if (isMatrixClientActive()) {
        matrixSyncDebugLog('runInitializeOnce → skip resetMatrixClient, client already syncing')
      } else {
        resetMatrixClient('initializeOnce')
      }
      initChatMessageSounds()

      applyMessengerBootChatListTab({ core: this })

      await this.fetchChats()

      if (generation !== matrixChatInitGeneration) {
        matrixSyncDebugLog('runInitializeOnce → aborted after fetchChats', { generation })
        return false
      }

      setMatrixSubscription('chat', subscribeMatrixChatUpdates(event => {
        if (event?.kind === 'membership' && event.roomId) {
          const leftOrBanned = event.membership
            && event.membership !== 'join'
            && event.membership !== 'invite'

          if (leftOrBanned) {
            rememberForgottenRoom(event.roomId)

            const chatId = this.resolveChatIdByMatrixRoomId(event.roomId)

            if (this.currentChat?.matrix_room_id === event.roomId) {
              this.currentChat = null
              clearMessages(this)
              this.activeTopic = null
              this.selectedTopicsGroupId = null
              this.showTopicsList = false
              clearMatrixSubscription('room')
            }

            dropRoomMessages(this, event.roomId)
            this.removeChatLocally(chatId)
            syncViewWithCore()
            return
          }

          // Join/invite комнаты, которая уже в каталоге — не долбим REST
          // (hydrate превью сам джойнит и иначе получается шторм silent fetchChats).
          const knownRoom = this.chats.some(
            chat => chat.matrix_room_id?.trim() === event.roomId,
          )
          if (knownRoom) {
            matrixSyncDebugLog('subscribeMatrixChatUpdates → skip REST (known room join)', {
              eventKind: event.kind,
              membership: event.membership,
              roomId: event.roomId,
            })
            return
          }
        }

        // Сообщения / last_message уже идут через subscribeMatrixIncomingMessages.
        // Name/topic/avatar state storms (metadata sync) не должны долбить Django REST.
        // Полный refetch каталога — только на structural changes (join/leave/new room).
        if (event?.kind === 'timeline') {
          matrixSyncDebugLog('subscribeMatrixChatUpdates → skip REST (timeline)', {
            eventKind: event.kind,
            eventType: event.eventType,
          })
          return
        }

        // Room appeared for a chat we already list — local preview hydrate is enough.
        if (event?.kind === 'room' && event.roomId) {
          const knownRoom = this.chats.some(
            chat => chat.matrix_room_id?.trim() === event.roomId,
          )
          if (knownRoom) {
            matrixSyncDebugLog('subscribeMatrixChatUpdates → skip REST (known room)', {
              eventKind: event.kind,
              roomId: event.roomId,
            })
            return
          }
        }

        scheduleSilentFetchChats(this)
      }))

      setMatrixSubscription('incoming', subscribeMatrixIncomingMessages(({ roomId, message, fromSelf }) => {
        // Только реально открытый UI чата; stale currentChat после ухода с /messenger не глушит toast.
        const viewedRoomId = resolveViewedMessageRoomId(this.$state)
        this.handleMatrixIncomingMessage(roomId, message, {
          bumpUnread: viewedRoomId !== roomId && !fromSelf,
        })
      }))

      setMatrixSubscription('peerDelete', subscribeMatrixPeerDeleteRequests(roomId => {
        void this.handleMatrixPeerDeleteRequest(roomId)
      }))

      setMatrixSubscription('redaction', subscribeMatrixMessageRedactions(({ roomId, redactedEventId }) => {
        void this.handleMatrixMessageRedaction(roomId, redactedEventId)
      }))

      setMatrixSubscription('presence', subscribeMatrixPresence((mxid, online) => {
        this.applyMatrixPresence(mxid, online)
      }))

      if (typeof document !== 'undefined' && !matrixVisibilityHandler) {
        matrixVisibilityHandler = () => {
          if (document.visibilityState !== 'visible') {
            return
          }

          if (isChatBeingViewed()) {
            scheduleSilentFetchChats(this)
          }

          if (isChatBeingViewed() && !shouldSuppressAutoReadReceipt()) {
            void this.acknowledgeMatrixReadReceipts()
          }

          void ensureMatrixSession().then(session => {
            if (!session) {
              return
            }
            void ensureMatrixSyncAlive().then(() => {
              void this.flushMessageOutbox()
            })
          })
        }
        document.addEventListener('visibilitychange', matrixVisibilityHandler)
      }

      if (typeof window !== 'undefined' && !matrixOnlineHandler) {
        matrixOnlineHandler = () => {
          void ensureMatrixSession().then(session => {
            if (!session) {
              return
            }
            void ensureMatrixSyncAlive().then(() => {
              void this.flushMessageOutbox()
            })
          })
        }
        window.addEventListener('online', matrixOnlineHandler)
      }

      this.isInitialized = true
      matrixSyncDebugLog('runInitializeOnce → done', { generation })
      this.scheduleMatrixUnreadResync()
      void this.flushMessageOutbox()
      void import('@/modules/chat-matrix-fast/services/matrixSygnalPusher')
        .then(({ ensureMatrixSygnalPusher }) => ensureMatrixSygnalPusher())
        .catch(() => {})
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (message === 'not authenticated') {
        return false
      }
      if (
        message.includes('сессии чата')
        || message.includes('Matrix-сессии')
        || message.includes('Matrix session')
      ) {
        this.needsMatrixAuth = true
        return false
      }

      this.matrixAuthError = message
      return false
    } finally {
      this.isInitializing = false
    }
  },

  resetInitialization(): void {
    matrixChatInitGeneration += 1
    matrixSyncDebugLog('resetInitialization', {
      generation: matrixChatInitGeneration,
    }, { stack: true })

    clearAllMatrixSubscriptions()
    resetMatrixPresenceState()
    matrixChatInitializationPromise = null
    if (matrixVisibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', matrixVisibilityHandler)
      matrixVisibilityHandler = null
    }
    if (matrixOnlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', matrixOnlineHandler)
      matrixOnlineHandler = null
    }
    this.needsMatrixAuth = false
    this.matrixAuthError = null

    this.isInitialized = false
    this.isInitializing = false
    this.chatCatalogRequestId += 1
    this.chatDirectories = []
    this.rootChatDirectoryIds = []
    this.ungroupedChatIds = []
    this.chatCatalogLoaded = false
    this.chatCatalogError = null
    this.chats = []
    this.currentChat = null
    clearMessages(this)
    clearAllRoomMessages(this)
    this.searchResults = null
    this.invitations = []
    this.pendingReactionOverrides = {}
    clearLocallyReadTopicRooms()

    // НО не очищаем localStorage - типы реакций одинаковые для всех пользователей
    this.reactionTypesLoaded = false
    this.reactionTypes = []

    globalUnreadMessages.resetUnread()

    clearMatrixSyncAuthRecoveryCircuit('resetInitialization')
    resetMatrixClient('resetInitialization')
  },

  syncActiveChatOnline(): void {
    notifyViewSyncActiveChatOnline()
  },

  applyMatrixPresence(mxid: string, online: boolean): void {
    const lastSeenAt = online ? null : getCachedMatrixLastSeenAt(mxid)
    let changed = false

    this.chats = this.chats.map(chat => {
      if (!chatMatchesPeerMxid(chat, mxid)) {
        return chat
      }

      if (chat.online === online && chat.last_seen_at === lastSeenAt) {
        return chat
      }

      changed = true
      return { ...chat, online, last_seen_at: lastSeenAt }
    })

    if (
      this.currentChat
        && chatMatchesPeerMxid(this.currentChat, mxid)
        && (this.currentChat.online !== online || this.currentChat.last_seen_at !== lastSeenAt)
    ) {
      this.currentChat = { ...this.currentChat, online, last_seen_at: lastSeenAt }
      changed = true
    }

    if (changed) {
      this.syncActiveChatOnline()
      syncViewWithCore()
    }
  },

  async refreshMatrixPresence(): Promise<void> {
    const peerMxids = collectDialogPeerMxids(this.chats)

    if (!peerMxids.length) {
      return
    }

    await trackMatrixPresence(peerMxids)

    let changed = false
    this.chats = this.chats.map(chat => {
      const peerMxid = resolveDialogPeerMxid(chat)
      if (!peerMxid) {
        return chat
      }

      const online = getCachedMatrixPresence(peerMxid)
      if (online === undefined) {
        return chat
      }

      const lastSeenAt = online ? null : getCachedMatrixLastSeenAt(peerMxid)
      if (chat.online === online && chat.last_seen_at === lastSeenAt) {
        return chat
      }

      changed = true
      return { ...chat, online, last_seen_at: lastSeenAt }
    })

    if (this.currentChat) {
      const peerMxid = resolveDialogPeerMxid(this.currentChat)
      if (peerMxid) {
        const online = getCachedMatrixPresence(peerMxid)
        if (online !== undefined) {
          const lastSeenAt = online ? null : getCachedMatrixLastSeenAt(peerMxid)
          if (this.currentChat.online !== online || this.currentChat.last_seen_at !== lastSeenAt) {
            this.currentChat = { ...this.currentChat, online, last_seen_at: lastSeenAt }
            changed = true
          }
        }
      }
    }

    if (changed) {
      this.syncActiveChatOnline()
      syncViewWithCore()
    }
  },
})
